import {
  type ShareUrlGeneratedBaseParams,
  type ShareUrlGeneratedComments,
  type ShareUrlGeneratedCustomFields,
  type ShareUrlGeneratedObjectType,
  type ShareUrlGeneratedVersion,
  type UseTrackGeneratedShareUrlParams,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

type ShortUrlForShareUrlGeneratedAnalytics = Pick<ShortUrlResponse, 'presentationSettings' | 'permissions'>;

export type BuildShareUrlGeneratedAnalyticsParamsInput = {
  shareId: string;
  shortUrl: ShortUrlForShareUrlGeneratedAnalytics;
  objectType: ShareUrlGeneratedObjectType;
  version?: ShareUrlGeneratedVersion;
};

const getMetadataVisible = (presentationSettings: ShortUrlForShareUrlGeneratedAnalytics['presentationSettings']) =>
  presentationSettings?.hideInfoTab !== true;

const getCustomFields = (
  showMetadata: boolean,
  permissions: ShortUrlForShareUrlGeneratedAnalytics['permissions'],
): ShareUrlGeneratedCustomFields => {
  if (!showMetadata) {
    return 'hide';
  }

  const { canView, canManageApplications } = permissions.customField;

  if (canView && canManageApplications) {
    return 'editable';
  }

  if (canView) {
    return 'read_only';
  }

  return 'hide';
};

const getComments = (permissions: ShortUrlForShareUrlGeneratedAnalytics['permissions']): ShareUrlGeneratedComments => {
  const { canViewDiscussions, canManageDiscussions } = permissions.asset;

  if (canViewDiscussions && canManageDiscussions) {
    return 'allow';
  }

  if (canViewDiscussions) {
    return 'read_only';
  }

  return 'hide';
};

const getBoardCollectionPresentationParams = (
  presentationSettings: ShortUrlForShareUrlGeneratedAnalytics['presentationSettings'],
) => {
  const lockedControls = presentationSettings?.lockedControls;

  return {
    hide_search_input: presentationSettings?.hideSearchBar === true,
    disable_expanding_nested: lockedControls?.expandNested === true,
    disable_view_settings:
      lockedControls?.viewType === true &&
      lockedControls?.cardSize === true &&
      lockedControls?.thumbnailStyle === true &&
      lockedControls?.metadataFieldsVisible === true,
  };
};

export const buildShareUrlGeneratedAnalyticsParams = ({
  shareId,
  shortUrl,
  objectType,
  version,
}: BuildShareUrlGeneratedAnalyticsParamsInput): UseTrackGeneratedShareUrlParams => {
  const metadata = getMetadataVisible(shortUrl.presentationSettings);
  const base: ShareUrlGeneratedBaseParams = {
    share_id: shareId,
    object_type: objectType,
    metadata,
    custom_fields: getCustomFields(metadata, shortUrl.permissions),
    comments: getComments(shortUrl.permissions),
    ...(version ? { version } : {}),
  };

  if (objectType === 'board' || objectType === 'collection') {
    return {
      ...base,
      object_type: objectType,
      ...getBoardCollectionPresentationParams(shortUrl.presentationSettings),
    };
  }

  return base;
};
